import { useReducer, useCallback } from 'react';

import { produce } from 'immer';

const ACTIONS = {
  ADD_ENTITIES: 'ADD_ENTITIES',
  ADD_LOADING_IDS: 'ADD_LOADING_IDS',
  ADD_ERROR_IDS: 'ADD_ERROR_IDS',
  ADD_MISSING_IDS: 'ADD_MISSING_IDS',
};

const initialState = {
  /**
   * This object holds array of entity data per entity domain, type and ID.
   *
   * Something like:
   * entitiesCache: {
   *    'autodesk-bim360-documentmanagement': {
   *      'documentlineage': {
   *        '1234': {
   *          entity: { // Entity fields },
   *          loading: false,
   *          error: null,
   *          retry: null,
   *        }
   *      }
   *    }
   * }
   */
  entitiesCache: {},
};

const entitiesReducer = (state = {}, action) => {
  switch (action.type) {
    case ACTIONS.ADD_LOADING_IDS: {
      const { ids } = action.payload;
      return ids.reduce((allEntities, currId) => {
        allEntities[currId] = {
          ...allEntities?.[currId],
          loading: true,
          error: null,
          retry: null,
        };
        return allEntities;
      }, state);
    }
    case ACTIONS.ADD_ENTITIES: {
      const { entities } = action.payload;
      return entities.reduce((allEntities, currEntity) => {
        allEntities[currEntity.id] = {
          ...allEntities?.[currEntity.id],
          entity: currEntity,
          loading: false,
          error: null,
          retry: null,
        };
        return allEntities;
      }, state);
    }
    case ACTIONS.ADD_MISSING_IDS: {
      const { ids } = action.payload;
      return ids.reduce((allEntities, currId) => {
        allEntities[currId] = {
          entity: null,
          loading: false,
          error: null,
          retry: null,
        };
        return allEntities;
      }, state);
    }
    case ACTIONS.ADD_ERROR_IDS: {
      const { ids, retry, error } = action.payload;
      return ids.reduce((allEntities, currId) => {
        allEntities[currId] = {
          ...allEntities?.[currId],
          loading: false,
          error,
          retry,
        };
        return allEntities;
      }, state);
    }
    default:
      throw new Error();
  }
};

const typeReducer = (nextState = {}, action) => {
  nextState[action.payload.type] = entitiesReducer(
    nextState[action.payload.type],
    action,
  );
  return nextState;
};

const domainReducer = (state = initialState, action) =>
  produce(state, (nextState) => {
    nextState.entitiesCache[action.payload.domain] = typeReducer(
      nextState.entitiesCache[action.payload.domain],
      action,
    );
  });

/**
 * This hook holds cached data of fetched entities.
 *
 * Returned value:
 * entitiesCache - an object of entities data by domain, type and ID. Each entity data consists of: {entity, loading, error, retry}.
 * addEntitiesToCache - callback to add entities to cache. Shape: ({ entities, domain, type }) => *.
 * addLoadingIds - callback to add entity IDs in loading state. Shape: ({ ids, domain, type }) => *.
 * addErrorIds - callback to add entity in error state. Shape: ({ ids, domain, type, error, retry }) => *.
 * handleRetries - callback to call unique retries for given entities. Needed as several entities might have the exact same error.
 *
 * @returns {{entitiesCache: *, addEntitiesToCache: *, addLoadingIds: *, addErrorIds: *, handleRetries: *}}
 */
export default () => {
  const [{ entitiesCache }, dispatch] = useReducer(domainReducer, initialState);

  const addEntitiesToCache = useCallback(
    ({ entities = [], requestIds = [], domain, type }) => {
      const missingIds = requestIds.filter(
        (id) => !entities.find((e) => e.id === id),
      );
      dispatch({
        type: ACTIONS.ADD_ENTITIES,
        payload: { domain, type, entities },
      });
      if (missingIds.length) {
        dispatch({
          type: ACTIONS.ADD_MISSING_IDS,
          payload: { domain, type, ids: missingIds },
        });
      }
    },
    [],
  );

  const updateEntitiesInCache = useCallback(
    ({ entities = [], domain, type }) => {
      dispatch({
        type: ACTIONS.ADD_ENTITIES,
        payload: { entities, domain, type },
      });
    },
    [],
  );

  const addLoadingIds = useCallback(
    ({ ids, domain, type }) =>
      dispatch({
        type: ACTIONS.ADD_LOADING_IDS,
        payload: { ids, domain, type },
      }),
    [],
  );

  const addErrorIds = useCallback(
    ({ ids, domain, type, error, retry }) =>
      dispatch({
        type: ACTIONS.ADD_ERROR_IDS,
        payload: { ids, domain, type, error, retry },
      }),
    [],
  );

  return {
    entitiesCache,
    addEntitiesToCache,
    updateEntitiesInCache,
    addLoadingIds,
    addErrorIds,
  };
};
